import {groupRosterPlayers} from "@/utils/rosterPlayerGroups";
import SeasonTimeline from "@/components/SeasonTimeline";
import RosterPanorama from "./RosterPanorama";
import {useEffect, useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate, useParams} from "react-router-dom";
import Page from "@/components/Page";
import Loading from "@/components/Loading";
import Breadcrumbs from "@/components/Breadcrumbs";
import Tabs from "@/components/Tabs";
import MainActions from "@/components/MainActions";
import ChoiceChips from "@/components/ChoiceChips";
import StatusBadge from "@/components/StatusBadge";
import VLFSelectNew from "@/components/Input/SelectNew";
import CardsList from "@/components/CardsList";
import EmptyData from "@/components/EmptyData";
import VLFModal from "@/components/Modal";

import {ModuleAPI} from "@/api/module";
import {RosterAPI} from "@/api/roster";
import {MatchAPI} from "@/api/match";
import {ContractAPI} from "@/api/contract";

import RosterActivityMatrices from "@/components/RosterActivityMatrices";

import {setTeamTab} from "@/actions";

import ArrowBack from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import StarBorderIcon from "@mui/icons-material/StarBorder";

import {
	ERROR_OPERATION,
	SUCCESSFUL_OPERATION,
	getInternalClubId,
} from "@/utils/constants";
import {getPlayerDetailPath, isInternalPlayer} from "@/utils/functions";

import RosterLineup from "@/components/RosterLineup";
import PrintIcon from "@mui/icons-material/Print";

import "./index.scss";
import {useTranslation} from "react-i18next";

function buildModuleLabel(md) {
	if (!md) return "—";
	const parts = [
		md.defenders,
		md.defensiveMidfielders,
		md.centralMidfielders,
		md.attackingMidfielders,
		md.attackers,
	].filter((n) => n != null && n > 0);
	return parts.length ? parts.join("-") : "—";
}

const playersStatusOptions = [
	{
		label: "Tutti",
		value: "all",
	},
	{
		label: "Rosa",
		value: ["Interno", "Prestito_in"],
	},
];

const ManagerFootballTeamPage = (props) => {
	const {handleNotification} = props;

	const dispatch = useDispatch();
	const navigate = useNavigate();
	const {id} = useParams();
	const {t} = useTranslation();

	const currentTab = useSelector((state) => state.teamTab);
	useEffect(() => {
		if (["stats", "panorama"].includes(currentTab))
			dispatch(setTeamTab("main_info"));
	}, [currentTab, dispatch]);
	const seasonId = useSelector((state) => state?.season?.id);

	const [loading, setLoading] = useState(false);
	const [team, setTeam] = useState();
	const [contractsMap, setContractsMap] = useState({});
	const [playersStatus, setPlayersStatus] = useState(playersStatusOptions[0]);
	const [lastLeagueMatches, setLastLeagueMatches] = useState([]);
	const [rosaView, setRosaView] = useState("field");
	const lineupRef = useRef(null);

	const [openHandleConfirm, setOpenHandleConfirm] = useState(false);

	// Moduli
	const [allModules, setAllModules] = useState([]);
	const [selectedModuleToAdd, setSelectedModuleToAdd] = useState(null);
	const contractsFetchedFor = useRef(null);
	const modulesFetchedFor = useRef(null);

	const getTeam = async () => {
		setLoading(true);
		try {
			const response = await RosterAPI.getById(id);
			setTeam(response);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const deleteTeam = async () => {
		setLoading(true);
		try {
			await RosterAPI.delete(id);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			navigate("/manager/teams");
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		} finally {
			setOpenHandleConfirm(false);
		}
	};

	const getAllModules = async () => {
		try {
			const response = await ModuleAPI.list();
			setAllModules(response);
		} catch (error) {
			// silenzioso: non blocca il resto della pagina
		}
	};

	const getAllContracts = async () => {
		try {
			const response = await ContractAPI.expiring(36500);
			const map = {};
			(response || []).forEach((c) => {
				if (c.playerId) {
					map[c.playerId] = c;
				}
			});
			setContractsMap(map);
		} catch (error) {
			// silenzioso: non blocca il resto della pagina
		}
	};

	useEffect(() => {
		getTeam();
	}, [id]); // eslint-disable-line react-hooks/exhaustive-deps

	// Lazy load: moduli e contratti quando serve
	useEffect(() => {
		if (
			(currentTab === "modules" || currentTab === "players") &&
			modulesFetchedFor.current !== id
		) {
			modulesFetchedFor.current = id;
			getAllModules();
		}
		if (
			(currentTab === "field" || currentTab === "players") &&
			contractsFetchedFor.current !== id
		) {
			contractsFetchedFor.current = id;
			getAllContracts();
		}
	}, [currentTab, id]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		setLastLeagueMatches([]);
		if (!team?.id || team.id !== id) return;
		let active = true;
		const fetchMatches = async () => {
			try {
				const response = await MatchAPI.list({
					seasonId: team?.season?.id || seasonId,
					participantRosterId: id,
					includeDetails: false,
				});
				const filtered = (response || [])
					.filter(
						(match) =>
							match.startDate &&
							(match.homeRosterId === id || match.awayRosterId === id),
					)
					.sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
				if (active) setLastLeagueMatches(filtered);
			} catch (error) {
				if (active)
					handleNotification(
						error.response?.data?.error || ERROR_OPERATION,
						"error",
					);
			}
		};
		fetchMatches();
		return () => {
			active = false;
		};
	}, [id, team?.id, team?.season?.id, seasonId]); // eslint-disable-line react-hooks/exhaustive-deps

	const goBack = () => navigate("/manager/teams");

	// --- Gestione moduli ---

	const assignedModuleIds = new Set(
		(team?.modules || []).map((m) => m.moduleId),
	);

	const availableModuleOptions = allModules
		.filter((m) => !assignedModuleIds.has(m.id))
		.map((m) => ({value: m.id, label: m.name}));

	const handleAddModule = async () => {
		if (!selectedModuleToAdd) return;
		setLoading(true);
		try {
			await RosterAPI.addModule({
				rosterId: id,
				moduleId: selectedModuleToAdd.value,
			});
			setSelectedModuleToAdd(null);
			await getTeam();
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		}
	};

	const handleRemoveModule = async (moduleId) => {
		setLoading(true);
		try {
			await RosterAPI.deleteModule(id, moduleId);
			await getTeam();
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		}
	};

	const handleSetDefaultModule = async (moduleId) => {
		setLoading(true);
		try {
			await RosterAPI.setDefaultModule(id, moduleId);
			await getTeam();
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		}
	};

	// --- Fine gestione moduli ---

	const handleUpdateJersey = async (element, number) => {
		const playerId = element?.player?.id || element?.playerId;
		if (!playerId) return;
		const parsed = parseInt(number);
		const jerseyNumber = !isNaN(parsed) ? parsed : null;
		setTeam((prev) => ({
			...prev,
			players: (prev?.players || []).map((p) =>
				(p.player?.id || p.playerId) === playerId ? {...p, jerseyNumber} : p,
			),
		}));
		try {
			await RosterAPI.addPlayers([{rosterId: id, playerId, jerseyNumber}]);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
			getTeam();
		}
	};

	const mainConfiguration = [
		{
			value: team?.name,
			label: "Nome",
			size: "col-6 col-lg-3",
		},
		{
			value: team?.season?.name,
			label: "Stagione",
			size: "col-6 col-lg-3",
		},
		{
			value: team?.competitionSeasons?.length
				? team.competitionSeasons
						.map(
							(cs) =>
								(cs.competition?.name || "") +
								(cs.season?.name ? ` (${cs.season.name})` : ""),
						)
						.join(", ")
				: "---",
			label: "Competizioni",
			size: "col-12 col-lg-3",
		},
	];

	const isInternal = team?.clubId === getInternalClubId() || !team?.clubId;
	useEffect(() => {
		if (team && !isInternal && ["trainings", "matches"].includes(currentTab)) {
			dispatch(setTeamTab("main_info"));
		}
	}, [team, isInternal, currentTab, dispatch]);

	const groupedPlayers = groupRosterPlayers(
		(team?.players || [])
			.filter(
				(p) =>
					playersStatus?.value === "all" ||
					playersStatus?.value?.indexOf(p.player?.status) !== -1,
			)
			.map((p) => ({
				...p,
				contractData: p.player?.id ? contractsMap[p.player.id] || null : null,
			})),
	);

	const playersListConfiguration = {
		data: groupedPlayers,
		onClick: (player) => navigate(getPlayerDetailPath(player?.player)),
		customClassName: (player) => {
			return player?.player?.status === "Prestito" ? "loan" : "";
		},
		...(isInternal ? {onJerseyEdit: handleUpdateJersey} : {}),
		separator: false,
	};

	const STAFF_UNIT_PRIORITY = ["generale", "tecnica"];
	const groupedStaff = (() => {
		const groups = {};
		(team?.staff || []).forEach((s) => {
			const unit = s.staff?.unit?.name || s.staff?.unit || "---";
			if (!groups[unit]) groups[unit] = [];
			groups[unit].push(s);
		});
		const ordered = {};
		STAFF_UNIT_PRIORITY.forEach((keyword) => {
			const key = Object.keys(groups).find((k) =>
				k.toLowerCase().includes(keyword),
			);
			if (key) ordered[key] = groups[key];
		});
		Object.keys(groups)
			.filter(
				(k) => !STAFF_UNIT_PRIORITY.some((kw) => k.toLowerCase().includes(kw)),
			)
			.sort()
			.forEach((k) => {
				ordered[k] = groups[k];
			});
		return ordered;
	})();

	const staffListConfiguration = {
		data: groupedStaff,
		onClick: (staff) =>
			staff?.staff.isInternal
				? navigate("/manager/staffs/" + staff?.staff.id)
				: navigate("/scouting/staffs/" + staff?.staff.id),
	};

	let mainActions = [];
	switch (currentTab) {
		case "main_info":
			mainActions = [
				{
					icon: <ArrowBack />,
					title: "Indietro",
					onClick: goBack,
					color: "info",
				},
				{
					icon: <EditIcon />,
					title: "Modifica",
					onClick: () => navigate("/manager/teams/" + team?.id + "/edit"),
				},
			];
			break;
		case "players":
			mainActions = [
				{
					icon: <ArrowBack />,
					title: "Indietro",
					onClick: goBack,
					color: "info",
				},
				...(isInternal
					? [
							{
								icon: <AddIcon />,
								title: "Gestisci giocatori",
								onClick: () => navigate("/manager/teams/" + id + "/players"),
							},
						]
					: []),
			];
			break;
		case "staff":
		case "staffs":
			mainActions = [
				{
					icon: <ArrowBack />,
					title: "Indietro",
					onClick: goBack,
					color: "info",
				},
				...(isInternal
					? [
							{
								icon: <AddIcon />,
								title: "Gestisci staff",
								onClick: () => navigate("/manager/teams/" + id + "/staffs"),
							},
						]
					: []),
			];
			break;
		case "modules":
			mainActions = [
				{
					icon: <ArrowBack />,
					title: "Indietro",
					onClick: goBack,
					color: "info",
				},
			];
			break;
		default:
			break;
	}

	const breadcrumbsElements = [
		{label: "Rose", path: "/manager/teams"},
		{label: "/"},
	];

	const identityHeader = (
		<section
			className="roster-overview__identity"
			aria-label="Informazioni principali"
		>
			{mainConfiguration.map((field, index) => (
				<div
					key={field.label}
					className={`roster-overview__field${index === 0 ? " roster-overview__field--name" : index === 2 ? " roster-overview__field--competitions" : ""}`}
				>
					{index !== 0 && <span>{field.label}</span>}
					<strong>{field.value || "—"}</strong>
				</div>
			))}
			<SeasonTimeline
				matches={lastLeagueMatches}
				rosterId={id}
				navigate={navigate}
				compact
				view="form"
			/>
		</section>
	);

	const tabs = [
		{
			key: "main_info",
			label: "Panoramica",
			render: () =>
				team?.id !== id ? null : isInternal ? (
					<RosterPanorama
						identityHeader={identityHeader}
						team={team}
						matches={lastLeagueMatches}
						navigate={navigate}
						selectTab={(tab) => dispatch(setTeamTab(tab))}
					/>
				) : (
					<div className="roster-overview">
						{identityHeader}
						<section className="roster-overview__timeline">
							<h3>La stagione gara per gara</h3>
							<SeasonTimeline
								matches={lastLeagueMatches}
								rosterId={id}
								navigate={navigate}
								compact
								view="timeline"
							/>
						</section>
					</div>
				),
		},
		{
			key: "players",
			label: t("players"),
			render: () => {
				const showFieldView = isInternal;
				const effectiveView = showFieldView ? rosaView : "list";
				return (
					<div className="row no-margin">
						<div className="col-sm-12 padding-small">
							<div className="d-flex align-items-center justify-content-between gap-2 mb-1">
								{showFieldView && (
									<ChoiceChips
										options={[
											{value: "field", label: "Campo"},
											{value: "list", label: "Elenco"},
										]}
										value={rosaView}
										onChange={setRosaView}
									/>
								)}
								{effectiveView === "field" && (
									<MainActions
										actions={[
											{
												icon: <PrintIcon />,
												label: "Stampa",
												title: "Stampa",
												onClick: () => lineupRef.current?.print(),
											},
										]}
									/>
								)}
								{effectiveView === "list" && isInternal && (
									<div className="filter-container">
										<VLFSelectNew
											options={playersStatusOptions}
											sorted={false}
											isClearable={false}
											value={playersStatus}
											onChange={(newValue) => setPlayersStatus(newValue)}
											noMargin
										/>
									</div>
								)}
							</div>
						</div>
						<div className="col-sm-12 padding-small">
							{effectiveView === "field" ? (
								team && (
									<RosterLineup
										ref={lineupRef}
										roster={{
											...team,
											players: (team.players || []).filter((p) =>
												isInternalPlayer(p.player),
											),
										}}
										modules={allModules}
										onOpenPlayer={(player) =>
											navigate(getPlayerDetailPath(player))
										}
										handleNotification={handleNotification}
										contracts={contractsMap}
										showJerseyNumbers
									/>
								)
							) : (
								<CardsList
									type="roster_player"
									grouped
									breadcrumbs
									tabbed
									customFilters
									listConfiguration={playersListConfiguration}
									defaultSortedField="player.lastName"
									groupSorted={false}
								/>
							)}
						</div>
					</div>
				);
			},
		},
		{
			key: "staffs",
			label: t("staff"),
			render: () => {
				return (
					<div className="row no-margin">
						<div className="col-sm-12 padding-small">
							<CardsList
								type="roster_staff"
								grouped
								groupSorted={false}
								breadcrumbs
								tabbed
								listConfiguration={staffListConfiguration}
								defaultSortedField="staff.lastName"
							/>
						</div>
					</div>
				);
			},
		},
		{
			key: "modules",
			label: "Moduli",
			render: () => {
				return (
					<div className="modules-tab">
						<div className="modules-tab__add">
							<VLFSelectNew
								options={availableModuleOptions}
								value={selectedModuleToAdd}
								onChange={(newValue) => setSelectedModuleToAdd(newValue)}
								placeholder="Aggiungi modulo..."
								noMargin
								sorted={false}
							/>
							<MainActions
								actions={[
									{
										icon: <AddIcon />,
										label: "Aggiungi",
										disabled: !selectedModuleToAdd,
										onClick: handleAddModule,
									},
								]}
							/>
						</div>
						{team?.modules?.length ? (
							<div className="modules-tab__list">
								{team.modules.map((m) => {
									const mod = m.module;
									const label = buildModuleLabel(mod);
									return (
										<div
											key={m.moduleId}
											className={
												"module-card" +
												(m.isDefault ? " module-card--default" : "")
											}
										>
											<div className="module-card__header">
												<span className="module-card__name">
													{mod?.name || label}
												</span>
												<span className="module-card__label">{label}</span>
											</div>
											<div className="module-card__positions">
												{[
													{label: "DIF", value: mod?.defenders},
													{label: "MED", value: mod?.defensiveMidfielders},
													{label: "CEN", value: mod?.centralMidfielders},
													{label: "TRQ", value: mod?.attackingMidfielders},
													{label: "ATT", value: mod?.attackers},
												]
													.filter((p) => p.value > 0)
													.map((p) => (
														<div key={p.label} className="module-card__pos">
															<span className="module-card__pos-value">
																{p.value}
															</span>
															<span className="module-card__pos-label">
																{p.label}
															</span>
														</div>
													))}
											</div>
											<div className="module-card__actions">
												{m.isDefault ? (
													<StatusBadge
														tone="good"
														label="Modulo di riferimento"
													/>
												) : (
													<MainActions
														actions={[
															{
																icon: <StarBorderIcon />,
																label: "Di riferimento",
																variant: "outlined",
																onClick: () =>
																	handleSetDefaultModule(m.moduleId),
															},
														]}
													/>
												)}
												<MainActions
													actions={[
														{
															icon: <DeleteIcon />,
															title: "Togli modulo",
															variant: "outlined",
															color: "error",
															onClick: () => handleRemoveModule(m.moduleId),
														},
													]}
												/>
											</div>
										</div>
									);
								})}
							</div>
						) : (
							<EmptyData />
						)}
					</div>
				);
			},
		},
		{
			key: "trainings",
			label: "Allenamenti",
			render: () => (
				<RosterActivityMatrices
					key="training"
					rosterId={id}
					season={team?.season}
					kind="training"
				/>
			),
		},
		{
			key: "matches",
			label: "Partite",
			render: () => (
				<RosterActivityMatrices
					key="match"
					rosterId={id}
					season={team?.season}
					kind="match"
				/>
			),
		},
	];

	const visibleTabs = tabs.filter((t) => {
		if (["modules", "trainings", "matches"].includes(t.key) && !isInternal)
			return false;
		return true;
	});

	return (
		<Page className="page-container roster-detail-page">
			<Loading visible={loading} />
			<Breadcrumbs
				elements={breadcrumbsElements}
				current={team?.name ?? ""}
				actions={<MainActions actions={mainActions} />}
			/>
			<Tabs
				freeHeight={["main_info", "trainings", "matches"].includes(currentTab)}
				tabs={visibleTabs}
				currentTab={currentTab}
				setCurrentTab={(tab) => dispatch(setTeamTab(tab))}
			/>
			<VLFModal
				open={openHandleConfirm}
				title="Eliminazione"
				content={
					<div className="mt-2">
						Sei sicuro di voler eliminare questo elemento?
					</div>
				}
				confirm={() => {
					deleteTeam();
				}}
				close={() => setOpenHandleConfirm(false)}
				confirmLabel="Conferma"
			/>
		</Page>
	);
};

export default ManagerFootballTeamPage;
